import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ChevronDown,
  LogOut,
  Shield,
  Building2,
  UserCircle
} from 'lucide-react';

const UserSelector: React.FC = () => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ✅ ELIMINADO: Código de carga de perfiles ya no necesario
  // El componente "Otros Perfiles del Sistema" fue eliminado

  const handleLogout = async () => {
    try {
      console.log('🔄 [UserSelector] Iniciando cierre de sesión...');
      // Cerrar el dropdown primero
      setIsOpen(false);
      // Esperar un momento para que el dropdown se cierre visualmente
      await new Promise(resolve => setTimeout(resolve, 100));
      // Ejecutar logout
      await logout();
      console.log('✅ [UserSelector] Sesión cerrada, redirigiendo...');
      // Usar window.location para forzar recarga completa y limpiar todo el estado
      window.location.replace('/');
    } catch (error) {
      console.error('❌ [UserSelector] Error al cerrar sesión:', error);
      // Intentar redirigir de todas formas
      setIsOpen(false);
      window.location.replace('/');
    }
  };

  const getRoleIcon = (rol: string | undefined) => {
    switch (rol) {
      case 'Administrador':
        return <Shield className="w-4 h-4 text-purple-600" />;
      case 'Contabilidad':
        return <Building2 className="w-4 h-4 text-blue-600" />;
      case 'Trabajador':
        return <UserCircle className="w-4 h-4 text-gray-600" />;
      default:
        return <UserCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getRoleColor = (rol: string | undefined) => {
    switch (rol) {
      case 'Administrador':
        return 'text-purple-700 bg-purple-50 border-purple-200';
      case 'Contabilidad':
        return 'text-blue-700 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  // Listener para detectar clicks fuera del dropdown (cuando está abierto)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const clickedElement = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement;
      
      // Verificar si el click es en un botón de alerta - NO cerrar en ese caso
      const isAlertButton = clickedElement?.id?.startsWith('dropdown-button-') || 
                           clickedElement?.id?.startsWith('boton-ver-') ||
                           clickedElement?.closest('[id^="dropdown-button-"]') !== null ||
                           clickedElement?.closest('[id^="boton-ver-"]') !== null ||
                           target.id?.startsWith('dropdown-button-') || 
                           target.id?.startsWith('boton-ver-') ||
                           target.closest('[id^="dropdown-button-"]') !== null ||
                           target.closest('[id^="boton-ver-"]') !== null;
      
      if (isAlertButton) {
        // NO cerrar si es un botón de alerta - dejar que el click pase normalmente
        return;
      }
      
      // Verificar si el click fue fuera del dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    // Agregar listener con un pequeño delay para evitar capturar el click que abre el dropdown
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div className="text-right">
          <p className="text-sm font-medium text-gray-900">{user.nombre || user.correo}</p>
          <p className="text-xs text-gray-500">{user.correo}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Overlay invisible con pointer-events: none para NO bloquear clicks */}
          {/* Usamos un listener en document para detectar clicks fuera en lugar de onClick del overlay */}
          <div
            className="fixed inset-0"
            style={{ 
              zIndex: 10,
              pointerEvents: 'none', // NO bloquear clicks - los botones pueden recibirlos normalmente
            }}
          />
          
          {/* Dropdown Menu */}
          <div 
            className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-20"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Información del Usuario Actual */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">
                    {(user.nombre || user.correo)?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">{user.nombre || 'Usuario'}</p>
                  <p className="text-xs text-gray-500">{user.correo}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {getRoleIcon(user.rol)}
                <span className={`text-xs px-2 py-1 rounded-full border ${getRoleColor(user.rol)}`}>
                  {user.rol}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2">J&J Operations</p>
            </div>

            {/* Información de Sesión */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <p className="text-xs font-semibold text-gray-700 mb-2">Información de Sesión</p>
              <div className="space-y-1 text-xs text-gray-600">
                {user.ultimo_login && (() => {
                  try {
                    // Intentar parsear la fecha (puede ser ISO string o formato localizado)
                    let fecha: Date;
                    if (user.ultimo_login.includes('T') || user.ultimo_login.includes('-')) {
                      // Es un ISO string o formato estándar
                      fecha = new Date(user.ultimo_login);
                    } else {
                      // Es un formato localizado, intentar parsearlo manualmente
                      // Formato esperado: "22/10/2025 15:42:00"
                      const partes = user.ultimo_login.match(/(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2}):(\d{2})/);
                      if (partes) {
                        fecha = new Date(
                          parseInt(partes[3]), // año
                          parseInt(partes[2]) - 1, // mes (0-indexed)
                          parseInt(partes[1]), // día
                          parseInt(partes[4]), // hora
                          parseInt(partes[5]), // minuto
                          parseInt(partes[6]) // segundo
                        );
                      } else {
                        fecha = new Date(user.ultimo_login);
                      }
                    }
                    
                    if (isNaN(fecha.getTime())) {
                      return <p className="text-gray-500">Último login: {user.ultimo_login}</p>;
                    }
                    
                    // Formatear la fecha correctamente
                    const fechaFormateada = fecha.toLocaleString('es-CL', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    });
                    
                    return <p className="text-gray-500">Último login: {fechaFormateada}</p>;
                  } catch (error) {
                    // Si falla el parseo, mostrar el valor original
                    return <p className="text-gray-500">Último login: {user.ultimo_login}</p>;
                  }
                })()}
              </div>
              <div className="mt-3 flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-xs text-green-700">Conectado con ERP Defontana</span>
              </div>
            </div>

            {/* Botón Cerrar Sesión */}
            <div className="p-4">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLogout();
                }}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center space-x-2 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserSelector;
